import { useState } from "react";
import BrandQR from "../components/BrandQR";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Ticket,
  ArrowLeft,
  Calendar,
  MapPin,
  ArrowUpRight,
  Search,
} from "lucide-react";

export default function MyTickets() {
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("email"); // "email" -> "otp"
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/tickets/lookup/request-otp", { email });
      setStep("otp");
      setResult(null);
      toast.success(
        data?.message ||
          "If paid tickets exist for that email, a verification code has been sent."
      );
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verifyAndLookup = async (e) => {
    e.preventDefault();
    if (!otp) return;
    setLoading(true);
    try {
      const { data } = await api.post("/tickets/lookup", { email, otp });
      setResult(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const resetToEmail = () => {
    setStep("email");
    setOtp("");
    setResult(null);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <nav className="bg-white border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandQR />
            <span className="font-heading font-semibold tracking-tight">
              Growithevent
            </span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-black transition-colors"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </nav>

      <main className="max-w-2xl mx-auto px-6 py-12">
        <p className="overline mb-3">Participant</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-semibold tracking-tighter mb-4">
          Find my ticket
        </h1>
        <p className="text-zinc-500 mb-10 leading-relaxed">
          Lost the link? Enter the email you used when you registered. To protect
          your privacy, we'll email you a verification code before showing your
          tickets.
        </p>

        {step === "email" ? (
          <form
            onSubmit={requestCode}
            className="bg-white border border-zinc-200 rounded-md p-6"
            data-testid="lookup-form"
          >
            <Label className="text-sm font-medium mb-2 block">
              Email used at registration
            </Label>
            <div className="flex gap-2">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-11 rounded-md border-zinc-200 focus-visible:ring-black"
                data-testid="lookup-email-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="h-11 bg-black text-white hover:bg-zinc-800 rounded-md px-5"
                data-testid="lookup-submit-button"
              >
                <Search className="w-4 h-4 mr-1.5" />
                {loading ? "Sending..." : "Send code"}
              </Button>
            </div>
          </form>
        ) : (
          <form
            onSubmit={verifyAndLookup}
            className="bg-white border border-zinc-200 rounded-md p-6"
            data-testid="lookup-otp-form"
          >
            <Label className="text-sm font-medium mb-2 block">
              Enter the 6-digit code sent to{" "}
              <span className="font-semibold text-zinc-900">{email}</span>
            </Label>
            <div className="flex gap-2">
              <Input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="123456"
                className="h-11 rounded-md border-zinc-200 focus-visible:ring-black tracking-widest"
                data-testid="lookup-otp-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="h-11 bg-black text-white hover:bg-zinc-800 rounded-md px-5"
                data-testid="lookup-verify-button"
              >
                <Ticket className="w-4 h-4 mr-1.5" />
                {loading ? "Verifying..." : "View tickets"}
              </Button>
            </div>
            <button
              type="button"
              onClick={resetToEmail}
              className="mt-3 text-xs text-zinc-500 hover:text-black transition-colors"
              data-testid="lookup-change-email"
            >
              Use a different email
            </button>
          </form>
        )}

        {result && (
          <div className="mt-8" data-testid="lookup-results">
            {result.count === 0 ? (
              <div
                className="border border-dashed border-zinc-300 rounded-md p-10 text-center bg-white"
                data-testid="no-tickets-found"
              >
                <Ticket
                  className="w-8 h-8 text-zinc-300 mx-auto mb-3"
                  strokeWidth={1.5}
                />
                <h3 className="font-heading text-xl font-semibold tracking-tight mb-2">
                  No tickets found
                </h3>
                <p className="text-sm text-zinc-500">
                  We couldn't find any paid tickets for{" "}
                  <span className="font-medium text-zinc-700">
                    {result.email}
                  </span>
                  . Check the email you used, or contact the organizer.
                </p>
              </div>
            ) : (
              <>
                <p className="overline mb-3">
                  {result.count} {result.count === 1 ? "ticket" : "tickets"}{" "}
                  found
                </p>
                <div className="space-y-3">
                  {result.tickets.map((t) => (
                    <Link
                      key={t.registration_id}
                      to={`/ticket/${t.registration_id}`}
                      className="block bg-white border border-zinc-200 rounded-md p-5 transition-all hover:-translate-y-0.5 hover:shadow-sm"
                      data-testid={`ticket-result-${t.registration_id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-heading text-lg font-semibold tracking-tight mb-2 truncate">
                            {t.event_name}
                          </h3>
                          <div className="space-y-1 text-xs text-zinc-600">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3 h-3" />
                              {new Date(t.event_date).toLocaleString([], {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })}
                            </div>
                            {t.event_location && (
                              <div className="flex items-center gap-1.5">
                                <MapPin className="w-3 h-3" />
                                {t.event_location}
                              </div>
                            )}
                            <div className="text-zinc-500">
                              Ticket holder:{" "}
                              <span className="text-zinc-900 font-medium">
                                {t.name}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2 flex-shrink-0">
                          {t.attendance_status === "present" ? (
                            <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 border-0 rounded-sm">
                              Checked-in
                            </Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-0 rounded-sm">
                              Paid
                            </Badge>
                          )}
                          <ArrowUpRight className="w-4 h-4 text-zinc-400" />
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
